import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Publish as PublishIcon,
  AutoAwesome as AIIcon,
  Article as ArticleIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { blogApi } from '../../../api/marketing';
import { Blog, BlogStats } from '../../../types/marketing.types';

const BlogsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [stats, setStats] = useState<BlogStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openAIDialog, setOpenAIDialog] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    tags: [] as string[],
    category: '',
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: [] as string[]
    }
  });
  const [aiPrompt, setAiPrompt] = useState({
    prompt: '',
    tone: 'professional' as 'professional' | 'casual' | 'technical' | 'friendly',
    length: 'medium' as 'short' | 'medium' | 'long',
    keywords: [] as string[]
  });

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await blogApi.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        status: statusFilter || undefined
      });

      if (response.success && response.data) {
        setBlogs(response.data);
        setTotalCount(response.pagination?.total || 0);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch blogs', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, statusFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchBlogs();
    fetchStats();
  }, [fetchBlogs]);

  const fetchStats = async () => {
    try {
      const response = await blogApi.getStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleOpenDialog = (blog?: Blog) => {
    if (blog) {
      setEditingBlog(blog);
      setFormData({
        title: blog.title,
        slug: blog.slug,
        content: blog.content,
        excerpt: blog.excerpt || '',
        tags: blog.tags,
        category: blog.category || '',
        seo: {
          metaTitle: blog.seo.metaTitle || '',
          metaDescription: blog.seo.metaDescription || '',
          keywords: blog.seo.keywords || []
        }
      });
    } else {
      setEditingBlog(null);
      setFormData({
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        tags: [],
        category: '',
        seo: {
          metaTitle: '',
          metaDescription: '',
          keywords: []
        }
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBlog(null);
  };

  const handleSave = async () => {
    try {
      if (editingBlog) {
        await blogApi.update(editingBlog._id, formData);
        enqueueSnackbar('Blog updated successfully', { variant: 'success' });
      } else {
        await blogApi.create(formData);
        enqueueSnackbar('Blog created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchBlogs();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to save blog', { variant: 'error' });
    }
  };

  const handleGenerateAI = async () => {
    try {
      setGeneratingAI(true);
      const response = await blogApi.generateWithAI(aiPrompt);
      
      if (response.success && response.data) {
        enqueueSnackbar('Blog generated successfully!', { variant: 'success' });
        setOpenAIDialog(false);
        fetchBlogs();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate blog', { variant: 'error' });
    } finally {
      setGeneratingAI(false);
    }
  };

  const handlePublish = async (id: string) => {
    try {
      await blogApi.publish(id);
      enqueueSnackbar('Blog published successfully', { variant: 'success' });
      fetchBlogs();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to publish blog', { variant: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) {
      return;
    }

    try {
      await blogApi.delete(id);
      enqueueSnackbar('Blog deleted successfully', { variant: 'success' });
      fetchBlogs();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to delete blog', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'success';
      case 'DRAFT':
        return 'warning';
      case 'ARCHIVED':
        return 'default';
      default:
        return 'default';
    }
  };

  const textFieldStyles = {
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
      '&.Mui-focused fieldset': { borderColor: 'white' }
    },
    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Blogs</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AIIcon />}
            onClick={() => setOpenAIDialog(true)}
          >
            Generate with AI
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Blog
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Total Blogs
                    </Typography>
                    <Typography variant="h4">{stats.totalBlogs}</Typography>
                  </Box>
                  <ArticleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Published
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {stats.publishedBlogs}
                    </Typography>
                  </Box>
                  <PublishIcon sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      AI Generated
                    </Typography>
                    <Typography variant="h4">{stats.aiGeneratedBlogs}</Typography>
                  </Box>
                  <AIIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Total Views
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {stats.totalViews}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search blogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PUBLISHED">Published</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="ARCHIVED">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Blogs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>AI Generated</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : blogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No blogs found
                </TableCell>
              </TableRow>
            ) : (
              blogs.map((blog) => (
                <TableRow key={blog._id}>
                  <TableCell>{blog.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={blog.status}
                      color={getStatusColor(blog.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{blog.category || '-'}</TableCell>
                  <TableCell>{blog.viewCount}</TableCell>
                  <TableCell>
                    {blog.aiGenerated ? (
                      <Chip icon={<AIIcon />} label="AI" size="small" color="secondary" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(blog.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    {blog.status === 'DRAFT' && (
                      <Tooltip title="Publish">
                        <IconButton
                          size="small"
                          onClick={() => handlePublish(blog._id)}
                        >
                          <PublishIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(blog)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(blog._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* AI Generation Dialog */}
      <Dialog 
        open={openAIDialog} 
        onClose={() => setOpenAIDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)'
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)'
            }
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>Generate Blog with AI</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Topic / Prompt"
              required
              fullWidth
              multiline
              rows={3}
              value={aiPrompt.prompt}
              onChange={(e) => setAiPrompt({ ...aiPrompt, prompt: e.target.value })}
              placeholder="e.g., Write about energy saving tips for businesses"
              sx={textFieldStyles}
            />
            <FormControl fullWidth sx={textFieldStyles}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Tone</InputLabel>
              <Select
                value={aiPrompt.tone}
                label="Tone"
                onChange={(e) => setAiPrompt({ ...aiPrompt, tone: e.target.value as any })}
                sx={{ color: 'white' }}
              >
                <MenuItem value="professional">Professional</MenuItem>
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
                <MenuItem value="friendly">Friendly</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={textFieldStyles}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Length</InputLabel>
              <Select
                value={aiPrompt.length}
                label="Length"
                onChange={(e) => setAiPrompt({ ...aiPrompt, length: e.target.value as any })}
                sx={{ color: 'white' }}
              >
                <MenuItem value="short">Short (500-800 words)</MenuItem>
                <MenuItem value="medium">Medium (1000-1500 words)</MenuItem>
                <MenuItem value="long">Long (2000-3000 words)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Keywords (comma separated)"
              fullWidth
              value={aiPrompt.keywords.join(', ')}
              onChange={(e) => setAiPrompt({
                ...aiPrompt,
                keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
              })}
              placeholder="energy, savings, business"
              sx={textFieldStyles}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAIDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleGenerateAI} 
            variant="contained" 
            disabled={!aiPrompt.prompt || generatingAI}
            startIcon={generatingAI ? <CircularProgress size={20} /> : <AIIcon />}
          >
            {generatingAI ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)'
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)'
            }
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          {editingBlog ? 'Edit Blog' : 'Create Blog'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Title"
              required
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              sx={textFieldStyles}
            />
            <TextField
              label="Slug"
              fullWidth
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              helperText="URL-friendly version of title (auto-generated if empty)"
              sx={textFieldStyles}
            />
            <TextField
              label="Content"
              required
              fullWidth
              multiline
              rows={10}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              sx={textFieldStyles}
            />
            <TextField
              label="Excerpt"
              fullWidth
              multiline
              rows={2}
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              sx={textFieldStyles}
            />
            <TextField
              label="Category"
              fullWidth
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              sx={textFieldStyles}
            />
            <TextField
              label="Tags (comma separated)"
              fullWidth
              value={formData.tags.join(', ')}
              onChange={(e) => setFormData({
                ...formData,
                tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
              })}
              sx={textFieldStyles}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.title || !formData.content}>
            {editingBlog ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlogsPage;
